import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { ShoppingCart, ArrowRight, Star, Check, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';
import NavBar from '../components/NavBar';
import SmartImage from '../components/SmartImage';

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState('products');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [addedToCart, setAddedToCart] = useState(false);

  useEffect(() => {
    loadProduct();
  }, [id]);

  useEffect(() => {
    if (currentPage === 'home') {
      navigate('/');
    } else if (currentPage === 'profile') {
      navigate('/profile');
    }
  }, [currentPage, navigate]);

  const loadProduct = async () => {
    setLoading(true);
    try {
      const productDoc = await getDoc(doc(db, COLLECTIONS.PRODUCTS, id));
      if (productDoc.exists()) {
        setProduct({ id: productDoc.id, ...productDoc.data() });
      }
    } catch (error) {
      console.error('Error loading product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product);
      setAddedToCart(true);
      setTimeout(() => setAddedToCart(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NavBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <div className="pt-24 flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-gray-100">
        <NavBar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
        />
        <div className="pt-24 text-center py-12">
          <Package className="w-24 h-24 mx-auto text-gray-300 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">المنتج غير موجود</h2>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-lg font-bold"
          >
            العودة للرئيسية
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <NavBar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />

      <div className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
          >
            <ArrowRight className="w-5 h-5" />
            العودة
          </button>

          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 p-8">

              <div className="flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-12">
                <SmartImage
                  src={product.image}
                  alt={product.name}
                  size="xlarge"
                  fallback="📦"
                />
              </div>

              <div className="space-y-6">
                {product.categoryName && (
                  <span className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                    {product.categoryName}
                  </span>
                )}

                <div>
                  <h1 className="text-4xl font-bold text-gray-900 mb-3">{product.name}</h1>
                  {product.brand && (
                    <p className="text-gray-600">العلامة التجارية: <span className="font-medium">{product.brand}</span></p>
                  )}
                </div>

                <div className="flex items-baseline gap-4">
                  <p className="text-5xl font-bold text-yellow-600">{product.price}</p>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${product.stock === 'متوفر' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                    {product.stock || 'متوفر'}
                  </span>
                </div>

                {product.description && (
                  <div className="border-t pt-6">
                    <h3 className="font-bold text-xl text-gray-900 mb-3">الوصف</h3>
                    <p className="text-gray-700 leading-relaxed">{product.description}</p>
                  </div>
                )}

                {product.features && product.features.length > 0 && (
                  <div className="border-t pt-6">
                    <h3 className="font-bold text-xl text-gray-900 mb-4">المميزات</h3>
                    <ul className="space-y-3">
                      {product.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-3 text-gray-700">
                          <div className="bg-green-100 rounded-full p-1">
                            <Check className="w-4 h-4 text-green-600" />
                          </div>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="border-t pt-6 space-y-4">
                  <button
                    onClick={handleAddToCart}
                    disabled={addedToCart}
                    className={`w-full py-4 rounded-xl font-bold text-lg flex items-center justify-center gap-3 transition-all ${addedToCart
                      ? 'bg-green-500 text-white'
                      : 'bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700 text-white shadow-lg'
                      }`}
                  >
                    {addedToCart ? (
                      <>
                        <Check className="w-6 h-6" />
                        تمت الإضافة للسلة
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="w-6 h-6" />
                        أضف إلى السلة
                      </>
                    )}
                  </button>

                  <button
                    onClick={() => navigate('/checkout')}
                    className="w-full bg-gray-800 hover:bg-gray-900 text-white font-bold py-4 rounded-xl transition-colors"
                  >
                    اشتري الآن
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
